import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import { selectIsEditMode } from 'features/common/editModeSlice';
import { COUPON_CLASSIFICATIONS } from 'features/MenuList/constants';
import { UpdateMobileOrderDisplayRequestPayload } from 'features/Store/models';
import storeSettingDetailService from 'features/Store/storeSettingDetailService';
import { FormEvent, useRef } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { displayError, handleServiceError } from 'utils';

export interface MobileOrderDisplayModalProps {
  posCodeId: number;
  posCode: string;
  restaurantId: number;
  restaurantName?: string;
  restaurantCode?: string;
  menuName: string;
  menuCode: string;
  couponClassification: number;
  defaultMobileOrderDisplayFlg: boolean;
  onClose?: (isDataChanged?: boolean) => void;
}

function MobileOrderDisplayModal({
  posCodeId,
  posCode,
  restaurantId,
  restaurantName,
  restaurantCode,
  menuName,
  menuCode,
  couponClassification,
  defaultMobileOrderDisplayFlg,
  onClose,
}: MobileOrderDisplayModalProps) {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();
  const formRef = useRef<HTMLFormElement>(null);

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  function handleClose() {
    onClose?.();
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();

    if (formRef.current) {
      const chkElement = formRef.current['mobile_order_display_flg'] as HTMLInputElement;
      const mobileOrderDisplayFlg = chkElement.checked;

      // compose payload
      const payload: UpdateMobileOrderDisplayRequestPayload = {
        pos_code_id: posCodeId,
        restaurant_id: restaurantId,
        mobile_order_display_flg: mobileOrderDisplayFlg,
      };

      (async () => {
        try {
          await storeSettingDetailService.updateMobileOrderDisplay(payload);
          onClose?.(true);
        } catch (e) {
          if (!handleServiceError(showGlobalModal, e as AxiosError)) {
            displayError(showGlobalModal, e as AxiosError);
          }
        }
      })();
    }
  }

  return (
    <Modal show backdrop="static" onHide={handleClose} centered>
      <Modal.Header closeButton className={`${isEditMode ? 'reedit-mode-wrapper' : ''}`}>
        <FeatureTitleBar title={t('MobileOrderDisplayModal.head_title')}></FeatureTitleBar>
      </Modal.Header>
      <Modal.Body className={`pb-3 ${isEditMode ? 'reedit-mode-wrapper' : ''}`}>
        <form ref={formRef} onSubmit={handleSubmit}>
          {restaurantCode && (
            <Row className="mb-4">
              <Col>
                {t('MobileOrderDisplayModal.store')}：&nbsp;{restaurantName}&nbsp;({restaurantCode})
              </Col>
            </Row>
          )}
          <Row>
            <Col>
              <div className="mb-3">{t('MobileOrderDisplayModal.menu_information')}</div>
              <div className="mb-3 ps-3">
                メニュー：&nbsp;&nbsp;{menuName}&nbsp;&nbsp;(
                {menuCode + (COUPON_CLASSIFICATIONS[couponClassification] || '')})
              </div>
            </Col>
          </Row>
          <Row className="mb-4">
            <Col sm={9}>
              <strong className="d-inline-block ps-3 text-large">
                POS：&nbsp;&nbsp;{menuName}&nbsp;&nbsp;({posCode})
              </strong>
            </Col>
            <Col sm={3}>
              <Form.Check
                type="switch"
                label=""
                name="mobile_order_display_flg"
                style={{ transform: 'scale(1.75)' }}
                defaultChecked={defaultMobileOrderDisplayFlg}
              />
            </Col>
          </Row>
          <Row>
            <Col className="text-end">
              <Button type="submit" className="btn--dark-green">
                {t('MobileOrderDisplayModal.save')}
              </Button>
            </Col>
          </Row>
        </form>
      </Modal.Body>
    </Modal>
  );
}

export default MobileOrderDisplayModal;
